import { useCallback, useState } from "react";

import { ObservableQuery } from "@apollo/client/core";
import { datadogRum } from "@datadog/browser-rum";
import { useShallow } from "zustand/react/shallow";

import { CommonListType, InboundingList, InboundingListStatus } from "__graphql__/types";
import {
  CollaborativeInboundProgressedAction,
  CollaborativeInboundProgressedMethod,
  CollaborativeInboundStage,
  CollaborativeInboundUpdateState,
  CollaborativeListPreparationMethod,
} from "analytics/events";
import { useFetchInboundPreDroppingProducts } from "flows/Inbound/stores/useInboundProductSearchService";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast, VARIANT_INFO_SUCCESS } from "shared/hooks/useCustomToast";

import { InboundingType } from "./inboundStore/types";
import { DRAFT_LIST_PERSIST_ID, useInboundStore } from "./inboundStore/useInboundStore";
import { extractInboundPlan, mapInboundingTypeToDeliveryType } from "./inboundStore/utils";
import { useInboundEventTracker } from "../hooks/useInboundEventTracker/useInboundEventTracker";
import {
  useDeleteInboundingListMutation,
  useDeleteInboundingListProductsMutation,
  useEditInboundingListMutation,
  useEditProductsInInboundingListMutation,
} from "../queries/collaborativeInbound/collaborativeInbound.generated";
import { useGetMultipleDesadvsRolliIdLazyQuery } from "../queries/despatchAdvice/despatchAdvice.generated";

const EDIT_SHARED_LIST_ERROR_TOAST_ID = "edit-shared-list-error-toast";
const EDIT_PREDROPPING_QUANTITY_ERROR_TOAST_ID = "edit-predropping-quanity-error-toast";
const EDIT_PREDROPPING_QUANTITY_SUCCESS_TOAST_ID = "edit-predropping-quantity-success-toast";
const DELETE_SHARED_LIST_SUCCESS_TOAST_ID = "delete-shared-list-success-toast";
const DELETE_SHARED_LIST_ERROR_TOAST_ID = "delete-shared-list-error-toast";
const DELETE_PRODUCT_ERROR_TOAST_ID = "delete-product-error-toast";

export const useEditSharedListName = () => {
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const { showToastUI } = useCustomToast();
  const setSharedListName = useInboundStore((state) => state.setSharedListName);

  const handleError = useCallback(
    (error: unknown, listId: string, name: string) => {
      setIsUpdatingName(false);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      datadogRum.addError("Failed to update shared list name", {
        listId,
        name,
        errorMessage,
      });
      showToastUI({
        id: EDIT_SHARED_LIST_ERROR_TOAST_ID,
        title: "edit-shared-list-error-toast",
      });
    },
    [showToastUI],
  );

  const [updateSharedListNameMutation] = useEditInboundingListMutation();

  const editSharedListName = useCallback(
    async (listId: string, name: string) => {
      try {
        setIsUpdatingName(true);
        await updateSharedListNameMutation({
          variables: { input: { listId, name } },
        });
        setSharedListName(name);
      } catch (error) {
        handleError(error, listId, name);
      } finally {
        setIsUpdatingName(false);
      }
    },
    [handleError, setSharedListName, updateSharedListNameMutation],
  );

  return { editSharedListName, isUpdatingName };
};

export const usePreDropQuantityUpdate = () => {
  const { sendSegmentTrackEvent } = useAnalytics();
  const [isUpdatingPreDropQuantity, setIsUpdatingPreDropQuantity] = useState(false);
  const [isHUUpdate, setIsUUpdate] = useState(false);
  const { showToastUI } = useCustomToast();

  const {
    deliverySSCC,
    sharedListName,
    inboundUnitsStockUpdates,
    inboundUnitsDisplayStates,
    updateInboundQuantityBySku,
    updateHandlingUnitSize,
  } = useInboundStore(
    useShallow((state) => ({
      deliverySSCC: state.deliverySSCC,
      sharedListName: state.sharedListName,
      inboundUnitsDisplayStates: state.inboundUnitsDisplayStates,
      inboundUnitsStockUpdates: state.inboundUnitsStockUpdates,
      updateInboundQuantityBySku: state.updateInboundQuantityBySku,
      updateHandlingUnitSize: state.updateHandlingUnitSize,
    })),
  );

  const [editPreDropQuantity, { error: editPreDropQuantityError }] =
    useEditProductsInInboundingListMutation();

  const handleError = useCallback(
    (error: unknown) => {
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
      datadogRum.addError("Edit Pre-dropping Quantity Error", { errorMessage });
      showToastUI({
        id: EDIT_PREDROPPING_QUANTITY_ERROR_TOAST_ID,
        title: "edit-predropping-quanity-error-toast",
      });
      setIsUpdatingPreDropQuantity(false);
    },
    [showToastUI],
  );

  const calculateNewQuantity = useCallback(
    (sku: string, oldValue: number, newValue: number): number => {
      const stockPlan = inboundUnitsStockUpdates[sku]?.stockUpdatePlan;
      const currentDelta = extractInboundPlan(stockPlan)?.quantityDelta ?? 0;
      if (oldValue === 0) return currentDelta * newValue;
      return (currentDelta / oldValue) * newValue;
    },
    [inboundUnitsStockUpdates],
  );

  const updatePreDropQuantity = useCallback(
    async ({
      listId,
      sku,
      oldValue,
      newValue,
      method,
      isHandlingUnit,
    }: {
      listId: string;
      sku: string;
      oldValue: number;
      newValue: number;
      method: CollaborativeInboundProgressedMethod;
      isHandlingUnit: boolean;
    }) => {
      const isHandlingUnitUpdate = method === CollaborativeInboundProgressedMethod.ManualHU;
      setIsUUpdate(isHandlingUnitUpdate);
      try {
        setIsUpdatingPreDropQuantity(true);
        const { unitSizeForDisplay } = inboundUnitsDisplayStates[sku];
        const quantity = isHandlingUnitUpdate
          ? calculateNewQuantity(sku, oldValue, newValue)
          : newValue * unitSizeForDisplay;

        const handlingUnitSize = isHandlingUnitUpdate ? newValue : unitSizeForDisplay;
        const { data } = await editPreDropQuantity({
          variables: {
            input: {
              listId,
              products: [{ sku, quantity, handlingUnitSize }],
            },
          },
        });

        if (data?.editProductsInInboundingList.success) {
          if (isHandlingUnitUpdate) {
            updateHandlingUnitSize(sku, newValue);
            showToastUI({
              id: EDIT_PREDROPPING_QUANTITY_SUCCESS_TOAST_ID,
              variant: VARIANT_INFO_SUCCESS,
              title: "edit-predropping-quantity-success-toast",
            });
          } else {
            updateInboundQuantityBySku(sku, newValue);
          }
          sendSegmentTrackEvent("inboundProgressed", {
            dropping_list_id: listId,
            product_sku: sku,
            action: CollaborativeInboundProgressedAction.ProductUpdatedQuantity,
            is_handling_unit: isHandlingUnit,
            method,
            quantity: isHandlingUnitUpdate
              ? newValue - oldValue
              : (newValue - oldValue) * unitSizeForDisplay,
            sscc: deliverySSCC,
            shelf_number: null,
            temporary_handling_unit_size: isHandlingUnitUpdate ? newValue : undefined,
            dropping_list_name: sharedListName,
            inbounding_stage: CollaborativeInboundStage.ListPreparation,
          });
        }
      } catch (error) {
        handleError(error);
      } finally {
        setIsUpdatingPreDropQuantity(false);
      }
    },
    [
      calculateNewQuantity,
      deliverySSCC,
      editPreDropQuantity,
      handleError,
      inboundUnitsDisplayStates,
      sendSegmentTrackEvent,
      sharedListName,
      showToastUI,
      updateHandlingUnitSize,
      updateInboundQuantityBySku,
    ],
  );

  return {
    updatePreDropQuantity,
    editPreDropQuantityError,
    isUpdatingPreDropQuantity,
    isHUUpdate,
  };
};

export const useDeleteSharedInboundList = (refetchInboundingLists: ObservableQuery["refetch"]) => {
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();

  const [isDeletingSharedInboundList, setIsDeletingSharedInboundList] = useState(false);
  const [deleteSharedInboundListMutation] = useDeleteInboundingListMutation({
    onCompleted: () => {
      showToastUI({
        status: "success",
        variant: "success",
        id: DELETE_SHARED_LIST_SUCCESS_TOAST_ID,
        title: "delete-shared-list-success-toast",
      });
    },
  });
  const resetPreDroppingState = useInboundStore((state) => state.resetPreDroppingState);
  const deleteSharedInboundList = useCallback(
    async (selectedList: InboundingList) => {
      setIsDeletingSharedInboundList(true);
      const {
        id: listId,
        status,
        listType,
        sscc = null,
        orderId = null,
        creatorId = null,
        inboundingType = null,
      } = selectedList;
      try {
        await deleteSharedInboundListMutation({
          variables: { input: { listId } },
        });
        const isCancelledOrder = listType === CommonListType.list_type_cancelled_order;
        const listPreparationMethod =
          isCancelledOrder || creatorId === "system"
            ? CollaborativeListPreparationMethod.Automatic
            : CollaborativeListPreparationMethod.Manual;

        sendSegmentTrackEvent("inboundStateUpdated", {
          dropping_list_id: listId,
          sscc,
          is_manual_unclaim: false,
          order_id: isCancelledOrder ? orderId : null,
          state: CollaborativeInboundUpdateState.DroppingListDeleted,
          inbounding_type: isCancelledOrder
            ? InboundingType.ReinboundedOrder
            : mapInboundingTypeToDeliveryType(inboundingType),
          list_preparation_method: listPreparationMethod,
        });
        // Handle draft cleanup
        if (status === InboundingListStatus.inbounding_list_status_draft) {
          resetPreDroppingState();
          localStorage.removeItem(DRAFT_LIST_PERSIST_ID);
        }
        await refetchInboundingLists();
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "An unknown error occurred while deleting the shared inbound list.";
        datadogRum.addError("Failed to delete the shared inbound list", { errorMessage });
        showToastUI({
          id: DELETE_SHARED_LIST_ERROR_TOAST_ID,
          title: "delete-shared-list-error-toast",
        });
      } finally {
        setIsDeletingSharedInboundList(false);
      }
    },
    [
      deleteSharedInboundListMutation,
      refetchInboundingLists,
      resetPreDroppingState,
      sendSegmentTrackEvent,
      showToastUI,
    ],
  );

  return { deleteSharedInboundList, isDeletingSharedInboundList };
};

export const useDeleteUnclaimedProduct = () => {
  const [isDeletingUnclaimedProduct, setIsDeletingUnclaimedProduct] = useState(false);
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();
  const { deliverySSCC, sharedListName, removeInboundUnitFromPreDroppingList } = useInboundStore(
    useShallow((state) => ({
      deliverySSCC: state.deliverySSCC,
      sharedListName: state.sharedListName,
      removeInboundUnitFromPreDroppingList: state.removeInboundUnitFromPreDroppingList,
    })),
  );

  const handleError = useCallback(
    (error: unknown) => {
      setIsDeletingUnclaimedProduct(false);
      const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
      datadogRum.addError("Failed to delete Unclaimed Product Error", { errorMessage });
      showToastUI({
        id: DELETE_PRODUCT_ERROR_TOAST_ID,
        title: "delete-product-error-toast",
      });
    },
    [showToastUI],
  );

  const [deleteInboundingListProducts] = useDeleteInboundingListProductsMutation();

  const deleteUnClaimedProduct = useCallback(
    async ({
      listId,
      sku,
      quantity,
      isHandlingUnit,
    }: {
      listId: string;
      sku: string;
      quantity: number;
      isHandlingUnit: boolean;
    }) => {
      try {
        setIsDeletingUnclaimedProduct(true);
        await deleteInboundingListProducts({
          variables: { input: { listId, skus: [sku] } },
        });
        sendSegmentTrackEvent("inboundProgressed", {
          dropping_list_id: listId,
          product_sku: sku,
          action: CollaborativeInboundProgressedAction.ProductRemovedFromList,
          is_handling_unit: isHandlingUnit,
          quantity,
          sscc: deliverySSCC,
          shelf_number: null,
          dropping_list_name: sharedListName,
          inbounding_stage: CollaborativeInboundStage.ListPreparation,
        });
        removeInboundUnitFromPreDroppingList(sku);
      } catch (error) {
        handleError(error);
      } finally {
        setIsDeletingUnclaimedProduct(false);
      }
    },
    [
      deleteInboundingListProducts,
      deliverySSCC,
      handleError,
      removeInboundUnitFromPreDroppingList,
      sendSegmentTrackEvent,
      sharedListName,
    ],
  );

  return { deleteUnClaimedProduct, isDeletingUnclaimedProduct };
};

export function useHandleDraftListContinuation() {
  const { sendInboundStateUpdate } = useInboundEventTracker();
  const { setDeliverySSCC, setSharedListId, setSharedListName, updateDespatchAdviceItems } =
    useInboundStore(
      useShallow((state) => ({
        setDeliverySSCC: state.setDeliverySSCC,
        setSharedListId: state.setSharedListId,
        setSharedListName: state.setSharedListName,
        updateDespatchAdviceItems: state.updateDespatchAdviceItems,
      })),
    );

  const [fetchDesadvs] = useGetMultipleDesadvsRolliIdLazyQuery({
    onCompleted: (data) =>
      updateDespatchAdviceItems(data?.getMultipleDesadvsRolliID?.despatchAdvices ?? null),
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : "An unknown error occurred.";
      datadogRum.addError("Error fetching despatch advices for delivery SSCC:", { errorMessage });
    },
  });

  const { fetchInboundPreDroppingProducts } = useFetchInboundPreDroppingProducts();

  const handleDraftListContinuation = useCallback(
    async (latestDraftList: InboundingList) => {
      sendInboundStateUpdate({
        state: CollaborativeInboundUpdateState.ListPreparationResumed,
        droppingListId: latestDraftList.id,
        sscc: latestDraftList.sscc,
        inboundingType: null,
        creatorId: latestDraftList.creatorId,
      });
      setSharedListId(latestDraftList.id);
      setSharedListName(latestDraftList.name);
      if (latestDraftList.sscc) {
        setDeliverySSCC(latestDraftList.sscc);
        await fetchDesadvs({ variables: { input: { rolliID: latestDraftList.sscc } } });
      }
      await fetchInboundPreDroppingProducts(latestDraftList.products);
    },
    [
      fetchDesadvs,
      fetchInboundPreDroppingProducts,
      sendInboundStateUpdate,
      setDeliverySSCC,
      setSharedListId,
      setSharedListName,
    ],
  );

  return { handleDraftListContinuation };
}
